import { AssessmentInfo, Html, Icf } from "../assessment";
import { Task, TaskPointScoring } from "../scoring/scoring";
import { SumScoring } from "../scoring/sumScoring";

export const bbsInfo: AssessmentInfo = {
    shortName: "BBS",
    fullName: "Berg Balance Scale",
    reportDescription: `\
<p>Die Berg Balance Scale (BBS) ist ein klinischer Test zur Bewertung des Gleichgewichts von Patienten, insbesondere bei älteren Menschen oder Personen mit neurologischen Beeinträchtigungen. Der Test umfasst 14 Aufgaben, die unterschiedliche Aspekte des Gleichgewichts in statischen und dynamischen Positionen überprüfen, z. B. das Stehen auf einem Bein, sich nach vorne beugen oder eine Drehung um die eigene Achse. Die maximale Punktzahl zeigt eine sehr gute Balance an, während niedrigere Werte auf ein erhöhtes Sturzrisiko und Gleichgewichtsschwierigkeiten hinweisen.</p>`,
}

export const bbsTaskList: Task[] = [
    {
        name: "Sitz zum Stand",
        icfCategories: ['d410'],
        taskReference: bbsTaskReference([
            "4: kann aufstehen ohne die Hände einzusetzen und sich selbstständig stabilisieren",
            "3: kann selbstständig mit Einsatz der Hände aufstehen",
            "2: kann nach einigen Versuchen mit Einsatz der Hände aufstehen",
            "1: braucht minimale Hilfe zum Aufstehen oder zum stabilisieren",
            "0: braucht mäßige bis maximale Hilfe um aufzustehen",
        ]),
    },
    {
        name: "Stehen ohne Unterstützung",
        icfCategories: ['d4154'],
        taskReference: bbsTaskReference([
            "4: kann 2 Min. sicher stehen",
            "3: kann 2 Min. unter Supervision stehen",
            "2: kann 30 Sek. ohne Unterstützung stehen",
            "1: braucht einige Versuche, um 30 Sek. ohne Unterstützung zu Stehen",
            "0: kann nicht ohne Unterstützung 30 Sek. stehen",
        ]),
    },
    {
        name: "Sitzen ohne Rückenlehne, beide Füße auf dem Boden",
        icfCategories: ['d4153'],
        taskReference: bbsTaskReference([
            "4: kann sicher und stabil 2 Min. sitzen",
            "3: kann 2 Min. unter Supervision sitzen",
            "2: kann 30 Sek. sitzen",
            "1: kann 10 Sek. sitzen",
            "0: kann nicht ohne Unterstützung 10 Sek. sitzen",
        ]),
    },
    {
        name: "Vom Stehen zum Sitzen",
        icfCategories: ['d410'],
        taskReference: bbsTaskReference([
            "4: setzt sich sicher mit minimalem Einsatz der Hände hin",
            "3: kontrolliert das Hinsetzen mit den Händen",
            "2: berührt mit Rückseite der Beine den Stuhl, um das Hinsetzen zu kontrollieren",
            "1: braucht 1 Person zur Hilfestellung",
            "0: braucht 2 Personen zur Hilfestellung oder Supervision um sicher zu sein",
        ]),
    },
    {
        name: "Transfer",
        icfCategories: ['d420'],
        taskReference: bbsTaskReference([
            "4: kann den Transfer sicher mit minimalem Einsatz der Hände auszuführen",
            "3: kann den Transfer sicher ausführen, muss aber die Hände einsetzen",
            "2: kann den Transfer mit verbaler Anweisung und/oder unter Supervision ausführen",
            "1: braucht 1 Person zur Hilfestellung",
            "0: braucht 2 Personen zur Hilfestellung oder Supervision um sicher zu sein",
        ]),
    },
    {
        name: "Stehen mit geschlossenen Augen ohne Unterstützung",
        icfCategories: ['d4154'],
        taskReference: bbsTaskReference([
            "4: kann 10 Sek. sicher stehen",
            "3: kann 10 Sek. unter Supervision stehen",
            "2: kann 3 Sek. stehen",
            "1: kann nicht die Augen 3 Sek. geschlossen halten, steht aber stabil",
            "0: braucht Hilfe, um nicht zu fallen",
        ]),
    },
    {
        name: "Stehen ohne Unterstützung mit geschlossenen Füßen",
        icfCategories: ['d4154'],
        taskReference: bbsTaskReference([
            "4: kann selbstständig Füße nebeneinander stellen und 1 Min. sicher stehen",
            "3: kann selbstständig Füße nebeneinander stellen und unter Supervision 1 Min. stehen",
            "2: kann selbstständig Füße nebeneinander stellen und die Position 30 Sek. halten",
            "1: braucht Hilfe um Position einzunehmen, kann aber 15 Sek. mit geschlossenen Füßen stehen",
            "0: braucht Hilfe um die Position einzunehmen, kann diese nicht für 15 Sek. halten",
        ]),
    },
    {
        name: "Im Stehen mit ausgestreckten Armen nach vorne reichen/langen",
        icfCategories: ['d4106'],
        taskReference: bbsTaskReference([
            "4: kann sicher mehr als 25 cm nach vorne langen/reichen",
            "3: kann sicher mehr als 12,5 cm nach vorne reichen/langen",
            "2: kann sicher mehr als 5 cm nach vorne reichen",
            "1: reicht nach vorne braucht aber Supervision",
            "0: verliert das Gleichgewicht beim Versuch/braucht externe Unterstützung",
        ]),
    },
    {
        name: "Aus dem Stand Gegenstand vom Boden aufheben",
        icfCategories: ['d4105', 'd4400'],
        taskReference: bbsTaskReference([
            "4: kann den Schuh sicher und mit Leichtigkeit aufheben",
            "3: kann den Schuh aufheben, braucht aber Supervision",
            "2: kann den Schuh nicht aufheben, reicht aber bis auf 2–5 cm an den Schuh heran und hält selbstständig das Gleichgewicht",
            "1: kann den Schuh nicht aufheben und braucht bei dem Versuch Supervision",
            "0: schon der Versuch scheitert oder braucht Hilfe um das Gleichgewicht nicht zu verlieren bzw. nicht zu fallen",
        ]),
    },
    {
        name: "Sicher im Stehen umdrehen, um nach hinten über die rechte und die linke Schulter zu schauen",
        icfCategories: ['d4154', 'd4106'],
        taskReference: bbsTaskReference([
            "4: schaut hinter sich über beide Seiten bei guter Gewichtsverlagerung",
            "3: schaut nur über eine Seite nach hinten, und zeigt weniger Gewichtsverlagerung auf der anderen Seite",
            "2: dreht sich nur zur Seite aber bewahrt das Gleichgewicht",
            "1: braucht Supervision beim Umdrehen",
            "0: braucht Hilfe um das Gleichgewicht nicht zu verlieren bzw. nicht zu fallen",
        ]),
    },
    {
        name: "Sich um 360° drehen",
        icfCategories: ['d4106'],
        taskReference: bbsTaskReference([
            "4: kann sich um 360° in 4 Sek. oder weniger drehen",
            "3: kann sich nur in einer Richtung sicher um 360° in 4 Sek. oder weniger drehen",
            "2: kann sich sicher um 360° drehen, aber langsam",
            "1: braucht nahe Supervision oder verbale Hilfestellung",
            "0: braucht Hilfe beim Drehen",
        ]),
    },
    {
        name: "Ohne Unterstützung abwechselnd die Füße auf eine Stufe stellen",
        icfCategories: ['d4106'],
        taskReference: bbsTaskReference([
            "4: kann sicher und selbstständig stehen und innerhalb von 20 Sek. die 8 Schrittfolgen bzw. -stufen stellen",
            "3: kann sicher selbstständig stehen und in mehr als 20 Sek. die 8 Schrittfolgen bzw. -stufen absolvieren",
            "2: kann 4 Schrittfolgen bzw. -stufen ohne Hilfe unter Supervision ",
            "1: kann mehr als 2 Schrittfolgen bzw. -stufen mit minimaler Hilfe absolvieren",
            "0: braucht Hilfe um nicht zu fallen oder schon der Versuch scheitert",
        ]),
    },
    {
        name: "Tandemstand: Stehen ohne Unterstützung mit einem Fuß vor dem anderen",
        icfCategories: ['d4154'],
        taskReference: bbsTaskReference([
            "4: kann selbstständig die Füße in den Tandemstand bringen und 30 Sek. Halten",
            "3: kann selbstständig einen Fuß vor den anderen stellen und diese Position 30 Sek. halten",
            "2: kann selbstständig einen kleinen Schritte nach vorne machen und diese Position 30 Sek. halten",
            "1: braucht Hilfe für den Schritt, kann aber Position 15 Sek. beibehalten",
            "0: verliert Gleichgewicht während des Schritts oder des Stehens",
        ]),
    },
    {
        name: "Auf einem Bein stehen",
        icfCategories: ['d4154', 'd4106'],
        taskReference: bbsTaskReference([
            "4: kann ein Bein selbstständig anheben und Position länger als 10 Sek. Halten",
            "3: kann ein Bein selbstständig anheben und Position für 5–10 Sek. Halten",
            "2: kann ein Bein selbstständig anheben und die Position 3 Sek. oder länger halten",
            "1: versucht ein Bein anzuheben, kann Position nicht 3 Sek. lang beibehalten, bleibt aber selbstständig stehen",
            "0: schon der Versuch scheitert oder Proband braucht Hilfe, um nicht zu fallen",
        ]),
    },
]

function bbsTaskReference(reference: Html[]): Html {
    return `<ul>${reference.map(entry => `<li>${entry}</li>`)}</ul>`
}

export const bbsScoreMapping: Icf[] = [
    Icf.Level4, // 0 points
    Icf.Level3, // 1 points
    Icf.Level2, // 2 points
    Icf.Level1, // 3 points
    Icf.Level0, // 4 points
]

export const bbsScoringInfo: TaskPointScoring = {
    scoreMapping: bbsScoreMapping,
    sided: false,
    tasks: bbsTaskList
}

export const bbsScoring: SumScoring = SumScoring.fromScoringInfo(bbsScoringInfo)